import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import ContainerWithBorder from 'components/atoms/ContainerWithBorder';
import MoneyCounter from 'components/atoms/MoneyCounter';
import ExpensesIcon from 'components/atoms/SVG/sidebarIcons/ExpensesIcon.component';
import AIChatSpinner from 'components/organisms/AIChat/AIChatMessageSkeleton';
import { sharedMarkdownComponents } from 'components/organisms/AIChat/utils';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import { useEffect, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { type YearlyReviewData } from 'types/global.types';
import { AIUsageConsentType } from 'types/users.types';
import BorderGradient from '../../BorderGradient';
import { getReviewYears } from '../../helpers';
import PercentageLabel from '../../PercentageLabel';
import DeductibleExpensesContentNoAI from './DeductibleExpensesContentNoAI';
import TopDeductibleExpensesCategory from './TopDeductibleExpensesCategory';
import useDudictibilitySuggestions from './useDudictibilitySuggestions';

const DeductibleExpensesContent: FC<{
  dudictibilitySuggestions: string[];
  loading: boolean;
  aiUsageConsent: AIUsageConsentType;
  containerRef: React.RefObject<HTMLDivElement>;
  rejectedConsent: boolean;
  setRejectedConsent: (value: boolean) => void;
}> = ({
  dudictibilitySuggestions,
  loading,
  aiUsageConsent,
  containerRef,
  rejectedConsent,
  setRejectedConsent,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [dudictibilitySuggestions, loading, containerRef]);

  // request consent if not allowed
  if (aiUsageConsent !== AIUsageConsentType.allow && !rejectedConsent) {
    return (
      <div className="mt-4 flex flex-col items-center text-center flex-1">
        <p>{t('yearly_review.deductible_expenses_widget.ai_consent_text')}</p>

        <div className="flex gap-4 mt-4 justify-center gap-2">
          <Button
            color="expenses"
            structure="primary"
            onClick={() => setRejectedConsent(true)}
            className="!bg-expenses-100 !text-expenses"
          >
            {t('yearly_review.deductible_expenses_widget.ai_consent_reject')}
          </Button>

          {/* TODO: update to use useAIUsageConsent */}
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm flex-1">
      {!dudictibilitySuggestions.length && loading ? (
        <div className="cursor-pointer self-center">
          <AIChatSpinner size="small" />
        </div>
      ) : (
        dudictibilitySuggestions?.map((paragraph, i) => (
          <ReactMarkdown
            key={paragraph + i}
            components={sharedMarkdownComponents}
          >
            {paragraph}
          </ReactMarkdown>
        ))
      )}
    </div>
  );
};

type DeductibleExpensesSectionProps = {
  data?: YearlyReviewData;
  rejectedConsent: boolean;
  setRejectedConsent: (value: boolean) => void;
};

const DeductibleExpensesSection: FC<DeductibleExpensesSectionProps> = ({
  data,
  rejectedConsent,
  setRejectedConsent,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const country = useCustomerCountry();

  const { thisYear, lastYear, tips } = data || {};

  const thisYearDeductibleExpensesTotal =
    thisYear?.deductibleExpenses?.total || 0;

  const lastYearDeductibleExpensesTotal =
    lastYear?.deductibleExpenses?.total || 0;

  const deductibleExpenseChangeRate =
    ((thisYearDeductibleExpensesTotal - lastYearDeductibleExpensesTotal) /
      lastYearDeductibleExpensesTotal) *
    100;

  const topCategories = Object.entries(thisYear?.categories?.top5 || {});

  const { previousYear } = getReviewYears();

  const { data: user } = useGetCurrentCustomerData();

  const scrollableRef = useRef<HTMLDivElement>(null);

  const { data: dudictibilitySuggestions, loading } =
    useDudictibilitySuggestions(
      lastYearDeductibleExpensesTotal,
      user?.aiUsageConsent || AIUsageConsentType.deny,
      scrollableRef,
    );

  const handleRejectConsent = () => {
    if (
      (user?.aiUsageConsent || AIUsageConsentType.deny) !==
        AIUsageConsentType.allow &&
      !rejectedConsent
    ) {
      setRejectedConsent(true);
    }
  };

  const handleUpdateConsent = () => {
    setRejectedConsent(false);
  };

  return (
    <ContainerWithBorder containerClassName="mt-8">
      <div className="p-6 flex gap-4">
        <div className="w-[50%] pr-6">
          <div className="flex items-center space-x-2 mb-10">
            <ExpensesIcon className="text-expenses stroke-white w-10 h-10 " />

            <h1 className="font-semibold text-xl text-primary">
              {t('yearly_review.deductible_expenses_widget.title')}
            </h1>
          </div>

          <div className="w-full text-center">
            <div className="font-avenir text-4xl mb-3 font-semibold">
              <MoneyCounter
                amount={thisYearDeductibleExpensesTotal}
                currency="EUR"
                stateId="yearlyReviewDeductibleExpensesTotal"
                noFractions
              />
            </div>

            <PercentageLabel
              value={deductibleExpenseChangeRate}
              comparisonLabel={previousYear}
            />
          </div>

          <div className="w-full mt-5 flex flex-col flex-1 justify-center">
            {topCategories.map(([key, value]) => (
              <TopDeductibleExpensesCategory
                key={key}
                amount={value.amount || 0}
                percentile={value.percentile || 0}
                categoryId={key}
                language={language}
              />
            ))}

            <div className="w-full mt-5 flex flex-col flex-1 justify-center">
              <div className="flex items-start bg-primary-25 rounded-lg p-4 border border-neutral-25">
                <ExclamationCircleIcon className="w-14 mr-4 text-primary-700 mt-2" />

                <p className="text-primary-700 text-sm">
                  {t(
                    `yearly_review.deductible_expenses_widget.alert_text.${country}`,
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* fallback to 2023 behavior */}
        {rejectedConsent ? (
          <DeductibleExpensesContentNoAI tips={tips || []} />
        ) : (
          <BorderGradient>
            <div
              ref={scrollableRef}
              className="flex-1 relative max-h-[640px] flex flex-col p-6 overflow-y-auto scroll-smooth"
            >
              <h1 className="font-semibold text-xl text-primary mb-7 flex items-center gap-2">
                {t(
                  'yearly_review.deductible_expenses_widget.to_be_deducted.title',
                )}
              </h1>

              <DeductibleExpensesContent
                dudictibilitySuggestions={dudictibilitySuggestions}
                loading={loading}
                aiUsageConsent={user?.aiUsageConsent || AIUsageConsentType.deny}
                containerRef={scrollableRef}
                rejectedConsent={rejectedConsent}
                setRejectedConsent={setRejectedConsent}
              />
            </div>
          </BorderGradient>
        )}
      </div>
    </ContainerWithBorder>
  );
};

export default DeductibleExpensesSection;
